import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, BellRing, CheckCheck, Smartphone } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { enablePush, pushSupported, pushAlreadyEnabled } from "@/lib/push";
import { toast } from "sonner";

export default function NotificationBell({ variant = "header" }) {
  const [notifs, setNotifs] = useState([]);
  const [pushOn, setPushOn] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    pushAlreadyEnabled().then(setPushOn);
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const unread = notifs.filter((n) => !n.read).length;
  const rail = variant === "rail";

  const open = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
      setNotifs((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.link) navigate(n.link);
  };

  const readAll = async () => {
    try { await api.post("/notifications/read-all"); } catch {}
    setNotifs((prev) => prev.map((x) => ({ ...x, read: true })));
    load();
  };

  const enable = async () => {
    try {
      const ok = await enablePush();
      setPushOn(ok);
      if (ok) toast.success("Notifications activées sur cet appareil");
      else toast.error("Autorisation refusée par le navigateur");
    } catch (e) {
      toast.error(e.message || "Notifications non supportées sur cet appareil");
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        {rail ? (
          <button data-testid="notifications-bell"
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors justify-center group-hover:justify-start text-slate-300 hover:bg-slate-800 hover:text-white">
            <span className="relative flex-shrink-0">
              {unread > 0 ? <BellRing className="w-4 h-4 text-blue-400" /> : <Bell className="w-4 h-4" />}
              {unread > 0 && (
                <span data-testid="notifications-badge"
                      className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {unread}
                </span>
              )}
            </span>
            <span className="hidden group-hover:inline whitespace-nowrap">Notifications</span>
          </button>
        ) : (
          <button data-testid="notifications-bell" className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors">
            {unread > 0 ? <BellRing className="w-5 h-5 text-blue-600" /> : <Bell className="w-5 h-5 text-slate-500" />}
            {unread > 0 && (
              <span data-testid="notifications-badge"
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {unread}
              </span>
            )}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" side={rail ? "right" : "bottom"} className="w-80 p-0 z-[1200]">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <span className="text-sm font-semibold text-slate-800">Notifications</span>
          <button onClick={readAll} data-testid="notifications-read-all"
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1">
            <CheckCheck className="w-3.5 h-3.5" />Tout marquer lu
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifs.length === 0 && (
            <div className="p-4 text-sm text-slate-500 text-center">Aucune notification</div>
          )}
          {notifs.map((n) => (
            <button key={n.id} onClick={() => open(n)} data-testid={`notification-${n.id}`}
                    className={`w-full text-left px-3 py-2.5 border-b border-slate-50 hover:bg-slate-50 ${n.read ? "opacity-60" : ""}`}>
              <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                {n.title}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{n.body}</div>
            </button>
          ))}
        </div>
        {pushSupported() && !pushOn && (
          <div className="p-2 border-t border-slate-100">
            <Button size="sm" variant="outline" onClick={enable} data-testid="push-enable-btn" className="w-full text-xs">
              <Smartphone className="w-3.5 h-3.5 mr-1.5" />Activer les notifications sur cet appareil
            </Button>
          </div>
        )}
        {pushOn && (
          <div className="p-2 border-t border-slate-100 text-center text-[11px] text-emerald-600">
            Notifications activées sur cet appareil
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
